import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Dados compartilhados entre Header e MobileBottomNav.
 *
 * Antes: cada componente fazia seu próprio fetch (Header com useQuery/key
 * própria e MobileBottomNav com useState+useEffect sem cache). A cada troca
 * de rota isso disparava 4-6 requests duplicados de `profiles` e
 * `arena_usuarios`.
 *
 * Agora: mesma queryKey em ambos → React Query dedupa, cache de 5 min,
 * sem refetch on window focus.
 */
export function useHeaderProfile() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["header:profile", user?.id],
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("full_name, avatar_url")
        .eq("id", user!.id)
        .maybeSingle();
      if (error) return null;
      return data as { full_name: string | null; avatar_url: string | null } | null;
    },
  });
}

export function useIsArenaOrProfessor() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["header:isArenaOrProfessor", user?.id],
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("arena_usuarios")
        .select("role_na_arena")
        .eq("usuario_id", user!.id)
        .in("role_na_arena", ["admin_arena", "professor"])
        .limit(1);
      if (error) return false;
      return !!data && data.length > 0;
    },
  });
}
